import React, { useState, useMemo, useEffect } from 'react';
import { Player, Tournament, GolfCourse } from '../types';
import { Download, ChevronUp, ChevronDown, Users, Trophy, Heart } from 'lucide-react';
import { MatchPlay } from '../types/matchplay';
import { supabase } from '../lib/supabase';
import { SponsorDisplay } from './sponsors/SponsorDisplay';
import { PlayerScoreBreakdown } from './PlayerScoreBreakdown';
import { MatchplayLeaderboardView } from './matchplay-v2/leaderboard/MatchplayLeaderboardView';

function exportTournamentResults(
  tournament: Tournament,
  players: Player[],
  course: GolfCourse | undefined,
  selectedTee: { name: string; rating: number; slope: number } | undefined
) {
  if (!course || !selectedTee) return;

  // Create CSV content
  let csvContent = 'data:text/csv;charset=utf-8,';

  // Add tournament info header
  csvContent += `Tournament: ${tournament.name}\n`;
  csvContent += `Date: ${tournament.startDate}\n`;
  csvContent += `Course: ${course.name}\n`;
  csvContent += `Tees: ${selectedTee.name} (Rating: ${selectedTee.rating}, Slope: ${selectedTee.slope})\n\n`;

  // Add column headers
  csvContent += 'Player,Flight,Handicap,';
  
  // Add hole numbers
  // Add hole numbers
  for (let i = 1; i <= 18; i++) {
    csvContent += `Hole ${i},`;
  }
  
  // Add summary columns
  csvContent += 'Out,In,Total,Net Total,Pairings Request\n';

  // Filter and sort players
  const tournamentPlayers = players
    .filter(p => p.tournamentId === tournament.id)
    .sort((a, b) => {
      // Sort by flight first
      const flightOrder = { 'Sr.': 1, 'A': 2, 'B': 3, 'C': 4 };
      const flightA = flightOrder[a.flight as keyof typeof flightOrder] || 5;
      const flightB = flightOrder[b.flight as keyof typeof flightOrder] || 5;
      if (flightA !== flightB) return flightA - flightB;
      
      // Then by name
      return a.name.localeCompare(b.name);
    });

  // Add player data
  tournamentPlayers.forEach(player => {
    const scores = player.scores || [];
    const courseHandicap = player.handicap 
      ? Math.round((player.handicap * selectedTee.slope) / 113)
      : 0;

    // Start with player info
    csvContent += `${player.name},${player.flight || ''},${player.handicap || 'SCR'},`;

    // Add hole-by-hole scores
    let frontNineTotal = 0;
    let backNineTotal = 0;
    let totalScore = 0;

    for (let hole = 1; hole <= 18; hole++) {
      const score = scores.find(s => s.hole === hole);
      const strokes = score?.strokes || '';
      csvContent += `${strokes},`;

      if (strokes) {
        if (hole <= 9) {
          frontNineTotal += strokes;
        } else {
          backNineTotal += strokes;
        }
        totalScore += strokes;
      }
    }

    // Calculate net total
    const netTotal = totalScore - courseHandicap;

    // Get pairings request (escape any commas)
    const pairingsRequest = player.pairingsRequest 
      ? `"${player.pairingsRequest.replace(/"/g, '""')}"` 
      : '';

    // Add totals and pairings request
    csvContent += [
      frontNineTotal || '',
      backNineTotal || '',
      totalScore || '',
      netTotal || '',
      pairingsRequest
    ].join(',') + '\n';
  });

  // Create download link
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${tournament.name.replace(/\s+/g, '_')}_results.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const formatDate = (dateStr: string) => {
  try {
    // Ensure consistent date handling by forcing UTC noon
    const date = new Date(`${dateStr.split('T')[0]}T12:00:00Z`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  } catch {
    return dateStr;
  }
};

interface LeaderboardProps {
  tournament: Tournament;
  courses: GolfCourse[];
  isAdmin: boolean;
  matches?: MatchPlay[];
  onTournamentUpdate: (tournament: Tournament) => void;
  activeTab: string;
}

type SortField = 'name' | 'flight' | 'gross' | 'net' | 'holes' | 'matches' | 'matchNo';
type SortDirection = 'asc' | 'desc';

export function Leaderboard({ 
  tournament,
  courses,
  isAdmin,
  matches = [],
  onTournamentUpdate,
  activeTab
}: LeaderboardProps) {
  // If this is a matchplay-v2 tournament, render the simplified leaderboard view
  if (tournament?.tournamentType === 'matchplay-v2') {
    return (
      <MatchplayLeaderboardView tournamentId={tournament.id} />
    );
  }

  const [showNet, setShowNet] = useState(true);
  const [selectedFlight, setSelectedFlight] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isManualSort, setIsManualSort] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [showCompletePrompt, setShowCompletePrompt] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [localPlayers, setLocalPlayers] = useState<Player[]>([]);
  const [divisionFilter, setDivisionFilter] = useState<'all' | 'senior' | 'regular'>('all');
  const [selectedPlayer, setSelectedPlayer] = useState<{name: string; scores: Record<number, number>} | null>(null);
  const [completedScores, setCompletedScores] = useState<Player[]>([]);

  // Fetch initial data and set up real-time subscription
  useEffect(() => {
    if (!tournament?.id) return;

    const fetchInitialData = async () => {
      try {
        // Fetch players for this tournament
        const { data: players, error: playersError } = await supabase
          .from('players')
          .select(`
            id, name, tournament_id, division, handicap, flight,
            team, match_no, starting_hole, scores, total_score
          `)
          .eq('tournament_id', tournament.id);

        if (playersError) throw playersError;

        const formattedPlayers = players.map(p => ({
          id: p.id,
          name: p.name,
          tournamentId: p.tournament_id,
          division: p.division,
          handicap: p.handicap,
          flight: p.flight,
          team: p.team,
          matchNo: p.match_no,
          startingHole: p.starting_hole,
          scores: p.scores || [],
          totalScore: p.total_score || 0
        }));

        setLocalPlayers(formattedPlayers);

        // Set up real-time subscription
        const channel = supabase
          .channel(`tournament-${tournament.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'players',
              filter: `tournament_id=eq.${tournament.id}`,
              columns: ['id', 'name', 'tournament_id', 'division', 'handicap', 'flight', 'team', 'match_no', 'starting_hole', 'scores', 'total_score']
            },
            async (payload) => {
              
              // Fetch latest player data
              const { data: updatedPlayers, error } = await supabase
                .from('players')
                .select(`
                  id, name, tournament_id, division, handicap, flight,
                  team, match_no, starting_hole, scores, total_score
                `)
                .eq('tournament_id', tournament.id);
                
              if (!error && updatedPlayers) {
                const formattedPlayers = updatedPlayers.map(p => ({
                  id: p.id,
                  name: p.name,
                  tournamentId: p.tournament_id,
                  division: p.division,
                  handicap: p.handicap,
                  flight: p.flight,
                  team: p.team,
                  matchNo: p.match_no,
                  startingHole: p.starting_hole,
                  scores: p.scores || [],
                  totalScore: p.total_score || 0
                }));
                
                setLocalPlayers(formattedPlayers);
                setLastUpdate(Date.now());
              }
            }
          )
          .subscribe();

        return () => {
          channel.unsubscribe();
        };
      } catch (error) {
        console.error('Error setting up leaderboard:', error);
        setSubscriptionError('Failed to load leaderboard data');
      }
    };

    fetchInitialData();
  }, [tournament?.id]);

  // Add effect to fetch completed scores
  useEffect(() => {
    const fetchCompletedScores = async () => {
      if (tournament.status === 'completed') {
        const { data, error } = await supabase
          .from('completed_tournament_scores')
          .select('*')
          .eq('tournament_id', tournament.id);

        if (error) {
          console.error('Error fetching completed scores:', error);
          return;
        }

        // Transform completed scores into Player format
        const transformedScores = data.map(score => ({
          id: score.player_id,
          name: score.player_name,
          tournamentId: score.tournament_id,
          scores: score.scores,
          totalScore: score.total_score,
          handicap: 0,
          flight: '',
          division: 'regular',
          team: '',
          matchNo: 0,
          startingHole: 1
        }));

        setCompletedScores(transformedScores);
      }
    };

    fetchCompletedScores();
  }, [tournament.id, tournament.status]);

  const flights = ['A', 'B', 'C'];
  const currentCourse = courses.find(c => c.id === tournament.courseId);
  const selectedTee = currentCourse?.tees.find(t => t.name === tournament.selectedTee);

  const handleSort = (field: SortField) => {
    setIsManualSort(true); // Mark that user has manually sorted
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline-block" />
    ) : (
      <ChevronDown className="w-4 h-4 inline-block" />
    );
  };

  const getMatchStatus = (match: MatchPlay, playerId: string) => {
    if (match.status === 'complete' && match.result) {
      return match.result;
    }

    const isTeam1 = match.team1.players.some(p => p.id === playerId);
    const team1Wins = match.holes.filter(h => h.winner === 'team1').length;
    const team2Wins = match.holes.filter(h => h.winner === 'team2').length;
    const holesPlayed = match.holes.length;
    
    if (holesPlayed === 0) return 'AS';

    const lead = team1Wins - team2Wins;
    if (lead === 0) return `AS thru ${holesPlayed}`;
    
    const absLead = Math.abs(lead);
    // Flip U/D based on whether the player is in team1 or team2
    const isUp = isTeam1 ? lead > 0 : lead < 0;
    return `${absLead}${isUp ? 'U' : 'D'} thru ${holesPlayed}`;
  };

  const getMatchStatusClass = (match: MatchPlay, playerId: string, status: string) => {
    if (match.status !== 'complete') {
      return 'bg-blue-100 text-blue-800';
    }

    const isTeam1 = match.team1.players.some(p => p.id === playerId);
    const team1Wins = match.holes.filter(h => h.winner === 'team1').length;
    const team2Wins = match.holes.filter(h => h.winner === 'team2').length;
    
    // For completed matches, determine if this player's team won
    const isWinner = isTeam1 ? team1Wins > team2Wins : team2Wins > team1Wins;
    
    return isWinner 
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  // Update the leaderboard sorting logic to implement our custom sorting
  const leaderboardData = useMemo(() => {
    
    // Use completed scores for completed tournaments, otherwise use players
    const dataSource = tournament.status === 'completed' ? completedScores : localPlayers;

    // Always filter by tournament first
    let filteredPlayers = dataSource.filter(p => p.tournamentId === tournament.id);

    // Apply division filter if not "all"
    if (divisionFilter !== 'all') {
      filteredPlayers = filteredPlayers.filter(p => p.division === divisionFilter);
    }

    // Then apply flight filter if needed
    if (selectedFlight !== 'all' && divisionFilter !== 'senior') {
      filteredPlayers = filteredPlayers.filter(p => p.flight === selectedFlight);
    }

    // Map players to leaderboard data
    const mappedData = filteredPlayers.map(player => {
      const playerScores = player.scores || [];
      // Filter scores for this tournament only
      const tournamentScores = tournament.status === 'completed' 
        ? playerScores // For completed tournaments, all scores are already for this tournament
        : playerScores.filter(score => score.tournamentId === tournament.id);
      
      let grossScore = 0;
      let totalGrossStrokes = 0;
      let netScore = 0;
      const completedHoles = tournamentScores.length;

      if (currentCourse) {
        // Calculate course handicap
        const courseHandicap = selectedTee && player.handicap
          ? Math.round((player.handicap * selectedTee.slope) / 113)
          : 0;

        // Calculate gross score relative to par
        grossScore = tournamentScores.reduce((total, score) => {
          const holePar = currentCourse.parValues[score.hole - 1];
          totalGrossStrokes += score.strokes;
          return total + (score.strokes - holePar);
        }, 0);

        // Calculate net score relative to par
        if (courseHandicap > 0) {
          netScore = grossScore - courseHandicap;
        }
      }

      // For completed tournaments, use the total_score if available
      if (tournament.status === 'completed' && 'totalScore' in player) {
        totalGrossStrokes = player.totalScore;
        if (currentCourse) {
          grossScore = totalGrossStrokes - currentCourse.parValues.reduce((sum, par) => sum + par, 0);
          netScore = grossScore; // Since we don't have handicap info in completed scores
        }
      }

      // Find player's match
      const playerMatch = matches.find(m => 
        m.team1.players.some(p => p.id === player.id) ||
        m.team2.players.some(p => p.id === player.id)
      );

      return {
        player,
        grossScore,
        netScore,
        holesPlayed: completedHoles,
        totalGrossStrokes,
        match: playerMatch,
        matchStatus: playerMatch ? getMatchStatus(playerMatch, player.id) : null
      };
    });

    // Sort based on tournament status and current sort settings
    return mappedData.sort((a, b) => {
      // If user has manually sorted, use their sort preference
      if (isManualSort) {
        const getValue = (item: typeof mappedData[0]) => {
          switch (sortField) {
            case 'gross': 
              return item.totalGrossStrokes;
            case 'name':
              return item.player.name;
            case 'flight':
              return item.player.flight || 'ZZZ';
            case 'net':
              return item.netScore;
            case 'holes':
              return item.holesPlayed;
            case 'matchNo':
              return item.player.matchNo || 999;
            default:
              return 0;
          }
        };

        const aValue = getValue(a);
        const bValue = getValue(b);

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return sortDirection === 'asc'
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }

      // Default sorting based on tournament status
      if (tournament.status === 'registration' || tournament.status === 'draft') {
        return a.player.name.localeCompare(b.player.name);
      }

      // For active or completed tournaments, sort by holes completed then gross score
      if (tournament.status === 'active' || tournament.status === 'completed') {
        // First sort by completed holes (most first)
        if (a.holesPlayed !== b.holesPlayed) {
          return b.holesPlayed - a.holesPlayed;
        }
        
        // Then by gross score (lowest first) for players with same number of holes
        return a.totalGrossStrokes - b.totalGrossStrokes;
      }

      // Fallback to name sorting
      return a.player.name.localeCompare(b.player.name);
    });
  }, [
    tournament.id, 
    tournament.status, 
    currentCourse, 
    selectedTee, 
    selectedFlight, 
    sortField, 
    sortDirection, 
    matches, 
    localPlayers, 
    divisionFilter, 
    isManualSort, 
    completedScores
  ]);

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-4">
        {/* Sponsor Display */}
        {tournament?.metadata?.sponsor && tournament.metadata.sponsor.active && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Heart className="w-4 h-4 text-primary-500" />
                Proudly Sponsored By
              </div>
            </div>
            <SponsorDisplay 
              sponsor={tournament.metadata.sponsor} 
              className="mt-2"
            />
          </div>
        )}

        <div className="flex justify-end items-center mb-4">
          {isAdmin && tournament?.status === 'active' && (
            <button
              onClick={() => setShowCompletePrompt(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center gap-2"
            >
              <Trophy className="w-4 h-4" />
              Complete Tournament
            </button>
          )}
          
          <div className="flex items-center gap-6">
            <select
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value as 'all' | 'senior' | 'regular')}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="all">Divisions</option>
              <option value="senior">Senior</option>
              <option value="regular">Regular</option>
            </select>
            <select
              value={selectedFlight}
              onChange={(e) => setSelectedFlight(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="all">Flights</option>
              {flights.map(flight => (
                <option key={flight} value={flight}>{flight} Flight</option>
              ))}
            </select>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => exportTournamentResults(
              tournament,
              localPlayers,
              currentCourse,
              selectedTee
            )}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Download className="w-4 h-4" />
            Export Results
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              {tournament?.format !== 'match' && (
                <th className="w-8 px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
              )}
              {tournament?.format === 'match' && (
                <th 
                  className="w-12 px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('matchNo')}
                >
                  Match {getSortIcon('matchNo')}
                </th>
              )}
              <th 
                className="w-24 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                Player {getSortIcon('name')}
              </th>
              {tournament?.format !== 'match' && (
                <>
                  <th 
                    className="w-16 px-1 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" 
                    onClick={() => handleSort('gross')}
                  >
                    Score {getSortIcon('gross')}
                  </th>
                  {showNet && (
                    <th 
                      className="w-16 px-1 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('net')}
                    >
                      Net {getSortIcon('net')}
                    </th>
                  )}
                  <th 
                    className="w-12 px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('holes')}
                  >
                    Thru {getSortIcon('holes')}
                  </th>
                </>
              )}
              {tournament.format === 'match' && (
                <th 
                  className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center justify-center gap-1">
                    <Trophy className="w-4 h-4" />
                    Match Status
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leaderboardData.map((data, index) => (
              <tr 
                key={data.player.id}
                className={`${index === 0 ? "bg-green-50" : ""} hover:bg-gray-50`}
              >
                {tournament?.format !== 'match' && (
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                    {index + 1}
                  </td>
                )}
                {tournament?.format === 'match' && (
                  <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                    {data.player.matchNo || '-'}
                  </td>
                )}
                <td className="px-1 py-2">
                  <div 
                    className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary-600"
                    onClick={() => {
                      setSelectedPlayer({
                        name: data.player.name,
                        scores: data.player.scores || []
                      });
                    }}
                  >
                    <div className="leading-tight">
                      {data.player.name.split(' ').map((part, i) => (
                        <div key={i}>{part}</div>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    HC: {data.player.handicap?.toFixed(2) || '0.00'}
                    {tournament?.format !== 'match' && data.player.flight && (
                      <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full">
                        {data.player.flight}
                      </span>
                    )}
                  </div>
                </td>
                {tournament?.format !== 'match' && (
                  <>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-right">
                      <span className={
                        data.grossScore === 0 ? 'text-gray-900' :
                        data.grossScore > 0 ? 'text-red-600' : 'text-green-600'
                      }>
                        {data.grossScore === 0 ? 'E' : (
                          data.grossScore > 0 ? `+${data.grossScore}` : data.grossScore
                        )}
                      </span>
                    </td>
                    {showNet && (
                      <td className="px-1 py-2 whitespace-nowrap text-sm text-right">
                        <span className={
                          data.netScore === 0 ? 'text-gray-900' :
                          data.netScore > 0 ? 'text-red-600' : 'text-green-600'
                        }>
                          {data.netScore === 0 ? 'E' : (
                            data.netScore > 0 ? `+${data.netScore}` : data.netScore
                          )}
                        </span>
                      </td>
                    )}
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                      {data.player.scores?.length || 0}
                    </td>
                  </>
                )}
                {tournament.format === 'match' && (
                  <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                    {data.matchStatus && data.match ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getMatchStatusClass(data.match, data.player.id, data.matchStatus)
                      }`}>
                        {data.matchStatus}
                      </span>
                    ) : '-'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Score Breakdown Modal */}
      {selectedPlayer && (
        <PlayerScoreBreakdown
          playerName={selectedPlayer.name}
          scores={selectedPlayer.scores}
          tournamentId={tournament.id}
          onClose={() => setSelectedPlayer(null)}
          course={courses.find(c => c.id === tournament.courseId)}
        />
      )}

      {/* Complete Tournament Confirmation Dialog */}
      {showCompletePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold">Complete Tournament</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to complete this tournament? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowCompletePrompt(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (tournament) {
                    onTournamentUpdate({
                      ...tournament,
                      status: 'completed',
                      endDate: new Date().toISOString().split('T')[0]
                    });
                    setShowCompletePrompt(false);
                  }
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
              >
                Complete Tournament
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}